import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

interface AuthContextValue {
  user: any | null;
  session: any | null;
  isAdmin: boolean;
  loading: boolean;
  aal: "aal1" | "aal2" | null;
  nextAal: "aal1" | "aal2" | null;
  mustChangePassword: boolean;
  refreshAal: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aal, setAal] = useState<"aal1" | "aal2" | null>("aal2"); // Default to aal2 (no MFA for now)
  const [nextAal, setNextAal] = useState<"aal1" | "aal2" | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const refreshAal = async () => {
    // No MFA support for now - always aal2
    setAal("aal2");
    setNextAal(null);
  };

  const refreshProfile = async () => {
    console.log('[AUTH] refreshProfile started');
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.log('[AUTH] refreshProfile: no token');
      setMustChangePassword(false);
      return;
    }
    
    try {
      console.log('[AUTH] refreshProfile: fetching session...');
      const response = await fetch('/api_php/session.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Session invalid');
      
      const data = await response.json();
      console.log('[AUTH] refreshProfile: session data received');
      setMustChangePassword(!!data.user?.must_change_password);
    } catch (err) {
      console.error('[AUTH] refreshProfile error:', err);
      setMustChangePassword(false);
    }
    console.log('[AUTH] refreshProfile completed');
  };

  const loadUserData = async (userId: string, token: string) => {
    console.log('[AUTH] loadUserData started for userId:', userId);
    try {
      console.log('[AUTH] Fetching user role...');
      const response = await fetch(`/api_php/rpc.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fn: 'getUserRole',
          args: { user_id: userId }
        })
      });
      
      const data = await response.json();
      console.log('[AUTH] User role received:', data?.role);
      setIsAdmin(data?.role === 'admin');
    } catch (err) {
      console.error('[AUTH] Error fetching role:', err);
      setIsAdmin(false);
    }
    
    console.log('[AUTH] Calling refreshAal...');
    await refreshAal();
    console.log('[AUTH] Calling refreshProfile...');
    await refreshProfile();
    console.log('[AUTH] loadUserData completed');
  };

  useEffect(() => {
    let mounted = true;

    // Check for existing session
    const checkAuth = async () => {
      console.log('[AUTH] Starting auth check...');
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('user');
      
      if (!token || !userStr) {
        console.log('[AUTH] No token or user found');
        setLoading(false);
        return;
      }

      try {
        console.log('[AUTH] Fetching session...');
        const response = await fetch('/api_php/session.php', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Session invalid');
        
        const sessionData = await response.json();
        console.log('[AUTH] Session valid');
        
        if (mounted) {
          const userData = JSON.parse(userStr);
          setUser(userData);
          setSession({ access_token: token, user: userData });
          console.log('[AUTH] Loading user data for ID:', userData.id);
          
          try {
            await loadUserData(userData.id, token);
            console.log('[AUTH] User data loaded successfully');
          } catch (err) {
            console.error('[AUTH] Failed to load user data:', err);
          }
          
          console.log('[AUTH] Setting loading to false');
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (mounted) {
          setUser(null);
          setSession(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const signOut = async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    window.location.href = '/login.html';
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading, aal, nextAal, mustChangePassword, refreshAal, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
